
import { defineComponent, PropType } from 'vue'
import type { ApolloQueryResult } from 'apollo-client'
import GetOwnerAiPolicy from '~/apollo/queries/owner/settings/GetOwnerAiPolicy.gql'
import {
  VulnerabilityRemediationFixPath,
  VulnerabilityRemediationFixPathUpdate,
  VulnerabilityRemediationPatchRunPullRequestStatus,
  VulnerabilityRemediationPatchRunStatus
} from '~/types/types'
import type { GetOwnerAiPolicyQuery, GetOwnerAiPolicyQueryVariables } from '~/types/types'
import { resolveNodes } from '~/utils/array'

export default defineComponent({
  name: 'RemediationSelector',
  props: {
    fixPaths: {
      type: Array as PropType<VulnerabilityRemediationFixPath[]>,
      required: true
    },
    selectedFixPathId: {
      type: String as PropType<VulnerabilityRemediationFixPath['id'] | null>,
      default: null
    },
    isCreatingPr: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      isAiAgentsEnabled: false,
      ownerId: ''
    }
  },
  async fetch() {
    const { owner, provider } = this.$route.params
    try {
      const response: ApolloQueryResult<GetOwnerAiPolicyQuery> = await this.$fetchGraphqlData(
        GetOwnerAiPolicy,
        {
          ownerLogin: owner,
          vcsProvider: this.$providerMetaMap[provider].value
        } as GetOwnerAiPolicyQueryVariables
      )
      const ownerId = response?.data?.owner?.id
      if (!ownerId) {
        throw new Error('Owner ID missing from AI policy response')
      }
      this.ownerId = ownerId
      this.isAiAgentsEnabled =
        response?.data?.owner?.ownerSetting?.aiPolicy?.isAiAgentsEnabled ?? false
    } catch (e) {
      this.$logErrorAndToast(e as Error, 'Failed to load AI policy settings.')
    }
  },
  computed: {
    selectedFixPath(): VulnerabilityRemediationFixPath | null {
      return this.fixPaths.find((fixPath) => fixPath.id === this.selectedFixPathId) ?? null
    },
    overallRiskScore() {
      const packageUpdates = this.getPackageUpdates(this.selectedFixPath)
      if (!packageUpdates.length) return null
      return Math.max(...packageUpdates.map((update) => update.overallRiskScore))
    },
    pullRequestUrl() {
      return this.selectedFixPath?.latestRemediationPatchRun?.vcsPrUrl ?? ''
    },
    commitUrl() {
      return this.selectedFixPath?.latestRemediationPatchRun?.vcsCommitUrl ?? ''
    },
    isPrViewable() {
      return Boolean(
        this.selectedFixPath?.latestRemediationPatchRun &&
        [
          VulnerabilityRemediationPatchRunPullRequestStatus.Pro,
          VulnerabilityRemediationPatchRunPullRequestStatus.Prm
        ].includes(this.selectedFixPath?.latestRemediationPatchRun.pullRequestStatus)
      )
    },
    isPrInProgress() {
      return (
        this.selectedFixPath?.latestRemediationPatchRun?.pullRequestStatus ===
        VulnerabilityRemediationPatchRunPullRequestStatus.Prp
      )
    },
    isRemediationRunRunning() {
      return (
        this.selectedFixPath?.latestRemediationPatchRun?.status ===
        VulnerabilityRemediationPatchRunStatus.Pend
      )
    }
  },
  emits: ['select-remediation', 'create-remediation'],
  methods: {
    getPackageUpdates(
      fixPath: VulnerabilityRemediationFixPath | null
    ): VulnerabilityRemediationFixPathUpdate[] {
      if (!fixPath) return []
      return resolveNodes(fixPath.packageUpdates)
    }
  }
})
